'use client';

import { collection, doc, getDocs } from 'firebase/firestore';
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { db } from '../config/firebaseConfig';
import { UserAuth } from '../context/authContext';
import DashboardLayout from './layout/DashboardLayout';

export default function Leaderboard() {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('blocksMined');
  const [leaderboardMode, setLeaderboardMode] = useState('room');
  const roomId = localStorage.getItem('roomId');
  const { userData } = UserAuth();
  const userCardRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const downloadLeaderboardCard = async () => {
    const currentUser = leaderboardData.find(
      (item) => item.publicKey === userData?.publicKey
    );

    if (!currentUser) {
      toast.error('Your card not found');
      return;
    }

    try {
      toast.loading('Generating image...');

      // Create canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Set canvas size
      canvas.width = 1000;
      canvas.height = 400;

      // Draw background gradient
      const gradient = ctx.createLinearGradient(
        0,
        0,
        canvas.width,
        canvas.height
      );
      gradient.addColorStop(0, '#1a0033');
      gradient.addColorStop(1, '#0f0f1e');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw border
      ctx.strokeStyle = 'rgba(124, 58, 237, 0.3)';
      ctx.lineWidth = 2;
      ctx.roundRect(20, 20, canvas.width - 40, canvas.height - 40, 15);
      ctx.stroke();

      // Set font for text
      ctx.fillStyle = 'white';
      ctx.font = 'bold 48px Arial';
      ctx.textAlign = 'left';

      // Draw rank badge
      const rankX = 60;
      const rankY = 80;
      const rankSize = 80;

      // Rank background
      let rankColor = '#1B1E3E';
      if (currentUser.rank === 1) {
        rankColor = '#fbbf24';
      } else if (currentUser.rank === 2) {
        rankColor = '#d1d5db';
      } else if (currentUser.rank === 3) {
        rankColor = '#fb923c';
      }

      ctx.fillStyle = rankColor;
      ctx.beginPath();
      ctx.arc(
        rankX + rankSize / 2,
        rankY + rankSize / 2,
        rankSize / 2,
        0,
        Math.PI * 2
      );
      ctx.fill();

      // Rank text
      ctx.fillStyle = currentUser.rank === 2 ? '#1f2937' : 'white';
      ctx.font = 'bold 50px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(
        currentUser.rank,
        rankX + rankSize / 2,
        rankY + rankSize / 2 + 20
      );

      // Draw user info
      ctx.fillStyle = 'white';
      ctx.textAlign = 'left';
      ctx.font = 'bold 36px Arial';
      ctx.fillText(currentUser.email?.split('@')[0] || 'User', 200, 120);

      // Draw stats
      const statsY = 220;
      const statWidth = (canvas.width - 100) / 3;
      const statX = [80, 80 + statWidth, 80 + statWidth * 2];

      const stats = [
        { label: 'Blocks Mined', value: currentUser.blocksMined },
        { label: 'TX Created', value: currentUser.transactionsCreated },
        { label: 'TX Verified', value: currentUser.transactionsVerified },
      ];

      stats.forEach((stat, index) => {
        // Stat box background
        ctx.fillStyle = 'rgba(30, 30, 62, 0.8)';
        ctx.fillRect(statX[index], statsY, statWidth - 20, 120);

        // Stat label
        ctx.fillStyle = '#a0a0b0';
        ctx.font = '12px Arial';
        ctx.fillText(stat.label, statX[index] + 15, statsY + 30);

        // Stat value
        ctx.fillStyle = '#7c3aed';
        ctx.font = 'bold 32px Arial';
        ctx.fillText(stat.value, statX[index] + 15, statsY + 80);
      });

      // Draw footer
      ctx.fillStyle = '#606070';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(
        `AstraPOS Leaderboard • Position #${currentUser.rank}`,
        canvas.width / 2,
        canvas.height - 20
      );

      // Convert to image and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `leaderboard-position-${userData?.email?.split('@')[0] || 'user'}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.dismiss();
        toast.success('Card downloaded successfully!');
      }, 'image/png');
    } catch (error) {
      console.error('Error downloading card:', error);
      toast.dismiss();
      toast.error('Failed to download card. Please try again.');
    }
  };

  const fetchRoomLeaderboard = useCallback(async () => {
    try {
      setLoading(true);

      if (leaderboardMode === 'global') {
        // GLOBAL MODE: Fetch all users and all rooms data
        const usersSnapshot = await getDocs(collection(db, 'users'));

        // Get all users with wallets
        const allUsers = [];
        usersSnapshot.forEach((userDoc) => {
          const user = userDoc.data();
          if (user.publicKey && user.isWalletCreated) {
            allUsers.push({
              publicKey: user.publicKey,
              email: user.email || 'Unknown',
              balance: user.balance || 0,
            });
          }
        });

        if (allUsers.length === 0) {
          setLeaderboardData([]);
          setLoading(false);
          return;
        }

        // Fetch all transactions and blocks across all rooms
        const roomsSnapshot = await getDocs(collection(db, 'rooms'));

        // Build stats map for all users
        const stats = {};
        allUsers.forEach((user) => {
          stats[user.publicKey] = {
            publicKey: user.publicKey,
            email: user.email,
            balance: user.balance,
            transactionsCreated: 0,
            transactionsVerified: 0,
            blocksMinedSet: new Set(),
          };
        });

        // Iterate through all rooms to collect stats
        for (const roomDoc of roomsSnapshot.docs) {
          const currentRoomId = roomDoc.id;

          const [txSnapshot, blocksSnapshot] = await Promise.all([
            getDocs(
              collection(doc(db, 'rooms', currentRoomId), 'transactions')
            ),
            getDocs(collection(doc(db, 'rooms', currentRoomId), 'blocks')),
          ]);

          // Count transactions created and verified (excluding system reward transactions)
          txSnapshot.forEach((txDoc) => {
            const tx = txDoc.data();

            // Skip system reward transactions
            if (
              tx.senderEmail === 'system' ||
              tx.sender === 'AstraMiner/AstraPOS/AstraHack'
            ) {
              return;
            }

            // Count transactions created
            if (stats[tx.sender]) {
              stats[tx.sender].transactionsCreated++;
            }

            // Count transactions verified
            if (tx.minerPublicKey && tx.verified && stats[tx.minerPublicKey]) {
              stats[tx.minerPublicKey].transactionsVerified++;

              // Track which blocks this miner contributed to
              if (tx.status === 'Added to chain') {
                // Find the block containing this transaction
                blocksSnapshot.forEach((blockDoc) => {
                  const block = blockDoc.data();
                  if (
                    block.transactions &&
                    block.transactions.some((t) => t.hash === tx.hash)
                  ) {
                    stats[tx.minerPublicKey].blocksMinedSet.add(
                      `${currentRoomId}-${block.hash}`
                    );
                  }
                });
              }
            }
          });
        }

        // Combine data
        let leaderboard = allUsers.map((user) => {
          const userStats = stats[user.publicKey] || {
            transactionsCreated: 0,
            transactionsVerified: 0,
            blocksMinedSet: new Set(),
          };

          return {
            publicKey: user.publicKey,
            email: user.email || 'Unknown',
            balance: user.balance || 0,
            transactionsCreated: userStats.transactionsCreated,
            transactionsVerified: userStats.transactionsVerified,
            blocksMined: userStats.blocksMinedSet.size,
          };
        });

        // Sort based on selected filter
        leaderboard.sort((a, b) => {
          if (selectedFilter === 'blocksMined') {
            return b.blocksMined - a.blocksMined;
          } else if (selectedFilter === 'transactionsCreated') {
            return b.transactionsCreated - a.transactionsCreated;
          } else if (selectedFilter === 'transactionsVerified') {
            return b.transactionsVerified - a.transactionsVerified;
          }
          return 0;
        });

        // Add rank
        leaderboard = leaderboard.map((item, index) => ({
          ...item,
          rank: index + 1,
        }));

        setLeaderboardData(leaderboard);
      } else {
        // ROOM MODE: Fetch only current room data

        // eslint-disable-next-line no-unused-vars
        const roomDoc = doc(db, 'rooms', roomId);
        const roomData = await getDocs(collection(db, 'users'));

        // Get participants and miners from current room
        let participants = [];
        let miners = [];
        roomData.forEach((userDoc) => {
          const user = userDoc.data();
          if (user.publicKey && user.isWalletCreated) {
            const userObj = {
              publicKey: user.publicKey,
              email: user.email || 'Unknown',
              balance: user.balance || 0,
            };

            // Check if user is in room's participants or miners
            if (
              user.roomParticipants &&
              user.roomParticipants.includes(roomId)
            ) {
              participants.push(userObj);
            }
            if (user.roomMiners && user.roomMiners.includes(roomId)) {
              miners.push(userObj);
            }
          }
        });

        // Get participants and miners arrays from room document
        const roomSnapshot = await getDocs(collection(db, 'rooms'));
        let currentRoom = null;
        roomSnapshot.forEach((doc) => {
          if (doc.id === roomId) {
            currentRoom = doc.data();
          }
        });

        if (currentRoom && currentRoom.participants) {
          participants = [];
          currentRoom.participants.forEach((publicKey) => {
            const user = roomData.docs.find(
              (u) =>
                u.data().publicKey === publicKey && u.data().isWalletCreated
            );
            if (user) {
              const userData = user.data();
              participants.push({
                publicKey: userData.publicKey,
                email: userData.email || 'Unknown',
                balance: userData.balance || 0,
              });
            }
          });
        }

        if (currentRoom && currentRoom.miners) {
          miners = [];
          currentRoom.miners.forEach((publicKey) => {
            const user = roomData.docs.find(
              (u) =>
                u.data().publicKey === publicKey && u.data().isWalletCreated
            );
            if (user) {
              const userData = user.data();
              miners.push({
                publicKey: userData.publicKey,
                email: userData.email || 'Unknown',
                balance: userData.balance || 0,
              });
            }
          });
        }

        // Combine participants and miners
        const roomUsers = [...participants, ...miners];

        if (roomUsers.length === 0) {
          setLeaderboardData([]);
          setLoading(false);
          return;
        }

        // Fetch transactions and blocks for current room only
        const [txSnapshot, blocksSnapshot] = await Promise.all([
          getDocs(collection(doc(db, 'rooms', roomId), 'transactions')),
          getDocs(collection(doc(db, 'rooms', roomId), 'blocks')),
        ]);

        // Build stats map for room users only
        const stats = {};
        roomUsers.forEach((user) => {
          stats[user.publicKey] = {
            publicKey: user.publicKey,
            email: user.email,
            balance: user.balance,
            transactionsCreated: 0,
            transactionsVerified: 0,
            blocksMinedSet: new Set(),
          };
        });

        // Count transactions created and verified (excluding system reward transactions)
        txSnapshot.forEach((txDoc) => {
          const tx = txDoc.data();

          // Skip system reward transactions
          if (
            tx.senderEmail === 'system' ||
            tx.sender === 'AstraMiner/AstraPOS/AstraHack'
          ) {
            return;
          }

          // Initialize sender stats if not present
          if (tx.sender && !stats[tx.sender]) {
            const userDoc = roomData.docs.find(
              (u) => u.data().publicKey === tx.sender
            );
            const userData = userDoc
              ? userDoc.data()
              : { email: tx.senderEmail || 'Unknown', balance: 0 };

            stats[tx.sender] = {
              publicKey: tx.sender,
              email: userData.email,
              balance: userData.balance || 0,
              transactionsCreated: 0,
              transactionsVerified: 0,
              blocksMinedSet: new Set(),
            };
          }

          // Count transactions created
          if (stats[tx.sender]) {
            stats[tx.sender].transactionsCreated++;
          }

          // Count transactions verified
          if (tx.minerPublicKey && tx.verified) {
            // Initialize miner stats if not present
            if (!stats[tx.minerPublicKey]) {
              const userDoc = roomData.docs.find(
                (u) => u.data().publicKey === tx.minerPublicKey
              );
              const userData = userDoc
                ? userDoc.data()
                : { email: tx.minerEmail || 'Unknown', balance: 0 };

              stats[tx.minerPublicKey] = {
                publicKey: tx.minerPublicKey,
                email: userData.email,
                balance: userData.balance || 0,
                transactionsCreated: 0,
                transactionsVerified: 0,
                blocksMinedSet: new Set(),
              };
            }

            if (stats[tx.minerPublicKey]) {
              stats[tx.minerPublicKey].transactionsVerified++;

              // Track which blocks this miner contributed to
              if (tx.status === 'Added to chain') {
                // Find the block containing this transaction
                blocksSnapshot.forEach((blockDoc) => {
                  const block = blockDoc.data();
                  if (
                    block.transactions &&
                    block.transactions.some((t) => t.hash === tx.hash)
                  ) {
                    stats[tx.minerPublicKey].blocksMinedSet.add(block.hash);
                  }
                });
              }
            }
          }
        });

        // Combine data for room users only
        let leaderboard = Object.values(stats).map((userStats) => {
          return {
            publicKey: userStats.publicKey,
            email: userStats.email || 'Unknown',
            balance: userStats.balance || 0,
            transactionsCreated: userStats.transactionsCreated,
            transactionsVerified: userStats.transactionsVerified,
            blocksMined: userStats.blocksMinedSet.size,
          };
        });

        // Sort based on selected filter
        leaderboard.sort((a, b) => {
          if (selectedFilter === 'blocksMined') {
            return b.blocksMined - a.blocksMined;
          } else if (selectedFilter === 'transactionsCreated') {
            return b.transactionsCreated - a.transactionsCreated;
          } else if (selectedFilter === 'transactionsVerified') {
            return b.transactionsVerified - a.transactionsVerified;
          }
          return 0;
        });

        // Add rank
        leaderboard = leaderboard.map((item, index) => ({
          ...item,
          rank: index + 1,
        }));

        setLeaderboardData(leaderboard);
      }

      setLoading(false);
    } catch (error) {
      toast.error('Failed to load leaderboard');
      console.error(error);
      setLoading(false);
    }
  }, [selectedFilter, leaderboardMode, roomId]);

  useEffect(() => {
    if (!roomId) {
      setLoading(false);
      return;
    }

    fetchRoomLeaderboard();
  }, [roomId, fetchRoomLeaderboard]);

  useEffect(() => {
    if (userCardRef.current && scrollContainerRef.current && !loading) {
      setTimeout(() => {
        const container = scrollContainerRef.current;
        const card = userCardRef.current;

        if (card && container) {
          const cardTop = card.offsetTop;
          const containerHeight = container.clientHeight;
          const cardHeight = card.clientHeight;
          const scrollTop = cardTop - containerHeight / 2 + cardHeight / 2;

          container.scrollTo({
            top: Math.max(0, scrollTop),
            behavior: 'smooth',
          });
        }
      }, 100);
    }
  }, [leaderboardData, loading]);

  const getFilterLabel = () => {
    switch (selectedFilter) {
      case 'blocksMined':
        return 'Blocks Mined';
      case 'transactionsCreated':
        return 'Transactions Created';
      case 'transactionsVerified':
        return 'Transactions Verified';
      default:
        return 'Blocks Mined';
    }
  };

  const getFilterValue = (item) => {
    switch (selectedFilter) {
      case 'blocksMined':
        return item.blocksMined;
      case 'transactionsCreated':
        return item.transactionsCreated;
      case 'transactionsVerified':
        return item.transactionsVerified;
      default:
        return item.blocksMined;
    }
  };

  if (!roomId) {
    return (
      <DashboardLayout>
        <section className="min-h-screen pt-10 pb-8 px-6">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl font-semibold text-white mb-6">
              Leaderboard
            </h1>
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-12 border border-white/10 shadow-purple-500/10 before:absolute before:inset-0 before:rounded-xl before:bg-gradient-to-br before:from-white/10 before:to-transparent before:opacity-50 before:pointer-events-none">
              <div className="relative z-10 text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2L15 8L22 9L17 14L18 21L12 18L6 21L7 14L2 9L9 8L12 2Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-purple-400"
                    />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">
                  Join a Room First
                </h2>
                <p className="text-white/60">
                  You need to be in a room to view the leaderboard
                </p>
              </div>
            </div>
          </div>
        </section>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <section className="min-h-screen pt-10 pb-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-white">Leaderboard</h1>
              <p className="text-white/60 text-sm mt-1">See where you stand</p>
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-xl rounded-xl p-8 border border-white/10 shadow-2xl shadow-purple-500/10 before:absolute before:inset-0 before:rounded-xl before:bg-gradient-to-br before:from-white/10 before:to-transparent before:opacity-50 before:pointer-events-none">
            <div className="relative z-10">
              {/* Room/Global Toggle */}
              <div className="mb-6">
                <label className="text-white/80 text-sm font-medium mb-3 block">
                  Leaderboard Type
                </label>
                <select
                  value={leaderboardMode}
                  onChange={(e) => setLeaderboardMode(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-[#1B1E3E]/60 text-white border border-white/10 focus:border-purple-500 focus:outline-none transition-all hover:bg-[#1B1E3E]/80"
                >
                  <option value="room">Room</option>
                  <option value="global">Global</option>
                </select>
              </div>

              {/* Filter Dropdown */}
              <div className="mb-6">
                <label className="text-white/80 text-sm font-medium mb-3 block">
                  Sort By
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setSelectedFilter('blocksMined')}
                    className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                      selectedFilter === 'blocksMined'
                        ? 'bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] text-white '
                        : 'bg-[#1B1E3E]/60 text-white/60 hover:bg-[#1B1E3E]/80 hover:text-white/80'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z" />
                      </svg>
                      <span className="text-sm">Blocks Mined</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setSelectedFilter('transactionsCreated')}
                    className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                      selectedFilter === 'transactionsCreated'
                        ? 'bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] text-white '
                        : 'bg-[#1B1E3E]/60 text-white/60 hover:bg-[#1B1E3E]/80 hover:text-white/80'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                      </svg>
                      <span className="text-sm">TX Created</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setSelectedFilter('transactionsVerified')}
                    className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                      selectedFilter === 'transactionsVerified'
                        ? 'bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] text-white '
                        : 'bg-[#1B1E3E]/60 text-white/60 hover:bg-[#1B1E3E]/80 hover:text-white/80'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                      </svg>
                      <span className="text-sm">TX Verified</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Leaderboard List */}
              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block w-10 h-10 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
                  <p className="text-white/60 text-sm mt-4">
                    Loading leaderboard...
                  </p>
                </div>
              ) : leaderboardData.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-white/60 text-sm">No data available yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between px-4 py-2 text-white/50 text-sm font-medium border-b border-white/10">
                    <div className="flex items-center gap-4 flex-1">
                      <span className="w-12">Rank</span>
                      <span className="flex-1">User</span>
                    </div>
                    <span className="w-32 text-right">{getFilterLabel()}</span>
                  </div>

                  <div
                    ref={scrollContainerRef}
                    className="space-y-2 max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar"
                    id="leaderboard-list"
                  >
                    {leaderboardData.map((item, index) => {
                      const isCurrentUser =
                        userData?.publicKey === item.publicKey;
                      return (
                        <div
                          key={item.publicKey}
                          ref={isCurrentUser ? userCardRef : null}
                          className={`flex items-center justify-between rounded-xl p-4 transition-all group ${
                            isCurrentUser
                              ? 'bg-gradient-to-r from-purple-500/30 to-blue-500/30 border border-purple-500/50'
                              : 'bg-[#1B1E3E]/60 hover:bg-[#1B1E3E]/80'
                          }`}
                        >
                          <div className="flex items-center gap-4 flex-1">
                            {/* Rank */}
                            <div
                              className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${
                                index === 0
                                  ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white'
                                  : index === 1
                                    ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-gray-800'
                                    : index === 2
                                      ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white'
                                      : 'bg-[#0F1229]/60 text-white/70'
                              }`}
                            >
                              {item.rank}
                            </div>

                            {/* Avatar and Info */}
                            <img
                              src={`https://api.dicebear.com/6.x/avataaars/svg?seed=${item.publicKey}`}
                              alt="avatar"
                              className="w-12 h-12 rounded-full ring-2 ring-purple-500/30"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="text-white font-semibold text-sm">
                                  {item.email?.split('@')[0] || 'Unknown'}
                                </h4>
                                {isCurrentUser && (
                                  <span className="text-xs px-2 py-1 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-full font-semibold">
                                    YOU
                                  </span>
                                )}
                              </div>
                              <p className="text-white/50 text-xs truncate">
                                {item.publicKey?.substring(0, 25)}...
                              </p>
                            </div>
                          </div>

                          {/* Score */}
                          <div className="w-32 text-right flex items-center gap-3">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-lg">
                              <span className="text-white font-bold text-lg">
                                {getFilterValue(item)}
                              </span>
                            </div>
                            {isCurrentUser && (
                              <button
                                onClick={downloadLeaderboardCard}
                                className="p-2 hover:bg-white/10 rounded-lg transition-all"
                                title="Download your card as JPG"
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  className="text-purple-400 hover:text-purple-300"
                                >
                                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                                  <polyline points="16 6 12 2 8 6" />
                                  <line x1="12" y1="2" x2="12" y2="15" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(27, 30, 62, 0.3);
          border-radius: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(124, 58, 237, 0.6);
          border-radius: 5px;
          border: 2px solid rgba(27, 30, 62, 0.3);
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(124, 58, 237, 0.9);
        }
      `}</style>
    </DashboardLayout>
  );
}
